<!-- Adapted from YesPlayMusic v0.4.11, MIT (qier222). views/explore.vue -->
<template>
  <div class="explore-page" aria-label="发现音乐" :aria-busy="loadingMore">
    <header class="explore-toolbar" aria-label="发现筛选">
    <CatalogPlatform />
    <div class="buttons" role="group" aria-label="发现分类">
      <button
        v-for="category in visibleCategories"
        :key="category"
        class="button"
        :class="{ active: category === activeCategory && !showCatOptions }"
        @click="goToCategory(category)"
      >
        {{ category }}
      </button>
      <button
        v-if="catalogSettings.platform !== 'tx'"
        class="button more"
        :class="{ active: showCatOptions }"
        aria-label="更多歌单分类"
        :aria-expanded="showCatOptions"
        @click="showCatOptions = !showCatOptions"
      >
        <svg-icon icon-class="more" />
      </button>
    </div>
    </header>

    <div v-show="showCatOptions" class="panel">
      <div v-for="bigCat in allBigCats" :key="bigCat" class="big-cat">
        <div class="name">{{ bigCat }}</div>
        <div class="cats">
          <div
            v-for="cat in getCatsByBigCat(bigCat)"
            :key="cat.name"
            class="cat"
            :class="{ active: settings.enabledPlaylistCategories.includes(cat.name) }"
          >
            <span role="button" tabindex="0" :aria-pressed="settings.enabledPlaylistCategories.includes(cat.name)" @click="toggleCat(cat.name)" @keydown.enter="toggleCat(cat.name)" @keydown.space.prevent="toggleCat(cat.name)">{{ cat.name }}</span>
          </div>
        </div>
      </div>
    </div>

    <p v-if="error" class="error-state" role="status">
      {{ error }} <button :disabled="loadingMore" @click="getPlaylist(playlists.length > 0)">重试</button>
    </p>
    <p v-if="loadingMore && !playlists.length" class="loading-state" role="status">正在加载歌单…</p>
    <div class="playlists">
      <CoverRow
        type="playlist"
        :items="playlists"
        :sub-text="subText"
        :show-play-count="activeCategory !== '排行榜'"
        :image-size="activeCategory !== '排行榜' ? 512 : 1024"
      />
      <p v-if="!loadingMore && !error && !playlists.length" class="empty-state">来源暂未返回此分类歌单。</p>
    </div>
    <div v-show="!['推荐歌单', '排行榜'].includes(activeCategory)" class="load-more">
      <ButtonTwoTone
        v-show="showLoadMoreButton && hasMore"
        color="grey"
        :disabled="loadingMore"
        @click.native="getPlaylist(true)"
      >{{ loadingMore ? '正在加载…' : '加载更多' }}</ButtonTwoTone>
    </div>
  </div>
</template>

<script>
import { runtime } from '../bridge';
import CatalogPlatform from '../components/CatalogPlatform.vue';
import { playlistCategories } from '../playlist-categories';
import CoverRow from '../components/CoverRow.vue';
import ButtonTwoTone from '../components/ButtonTwoTone.vue';

const categoriesKey = 'mineradio.ypm.playlist-categories.v1';

function enabledCategories() {
  const defaults = playlistCategories.filter(category => category.enable).map(category => category.name);
  try {
    const saved = JSON.parse(localStorage.getItem(categoriesKey) || 'null');
    if (!Array.isArray(saved)) return defaults;
    const enabled = new Set(saved.filter(name => playlistCategories.some(category => category.name === name)));
    playlistCategories.filter(category => category.bigCat === 'static').forEach(category => enabled.add(category.name));
    return playlistCategories.filter(category => enabled.has(category.name)).map(category => category.name);
  } catch {
    return defaults;
  }
}

export default {
  name: 'Explore',
  components: { CoverRow, ButtonTwoTone, CatalogPlatform },
  data() {
    return {
      settings: { enabledPlaylistCategories: enabledCategories() },
      catalogSettings: runtime.catalogSettings,
      playlists: [],
      loadingMore: false,
      showLoadMoreButton: false,
      hasMore: false,
      allBigCats: ['语种', '风格', '场景', '情感', '主题'],
      showCatOptions: false,
      cursor: 0,
      error: '',
      request: 0,
    };
  },
  computed: {
    visibleCategories() { return this.catalogSettings.platform === 'tx' ? ['全部', '推荐歌单', '排行榜'] : this.settings.enabledPlaylistCategories; },
    activeCategory() {
      const category = this.$route.query.category;
      if (this.catalogSettings.platform === 'tx') return ['全部', '推荐歌单', '排行榜'].includes(category) ? category : '全部';
      return playlistCategories.some(item => item.name === category) ? category : '全部';
    },
    subText() {
      if (this.activeCategory === '排行榜') return 'updateFrequency';
      if (this.activeCategory === '推荐歌单') return 'copywriter';
      return 'none';
    },
  },
  watch: {
    'catalogSettings.platform'() { this.showCatOptions = false; this.getPlaylist(false); },
    activeCategory() {
      this.getPlaylist(false);
    },
  },
  created() {
    this.getPlaylist(false);
  },
  beforeDestroy() {
    this.request += 1;
  },
  methods: {
    goToCategory(category) {
      this.showCatOptions = false;
      if (category === this.activeCategory) return;
      this.$router.push({ name: 'explore', query: { category } });
    },
    getCatsByBigCat(name) {
      return playlistCategories.filter(category => category.bigCat === name);
    },
    toggleCat(name) {
      const enabled = this.settings.enabledPlaylistCategories;
      const selected = enabled.includes(name) ? enabled.filter(category => category !== name) : [...enabled, name];
      this.settings.enabledPlaylistCategories = playlistCategories.filter(category => selected.includes(category.name)).map(category => category.name);
      try {
        localStorage.setItem(categoriesKey, JSON.stringify(this.settings.enabledPlaylistCategories));
      } catch {
        runtime.notify('浏览器无法保存分类设置，本次会话仍可使用。');
      }
    },
    async getPlaylist(append = false) {
      if (append && this.loadingMore) return;
      const version = ++this.request;
      this.loadingMore = true;
      this.error = '';
      if (!append) {
        this.playlists = [];
        this.cursor = 0;
        this.hasMore = false;
        this.showLoadMoreButton = false;
      }
      try {
        let data;
        if (this.activeCategory === '推荐歌单') {
          data = await runtime.catalog('recommended', undefined, { limit: 100 });
        } else if (this.activeCategory === '排行榜') {
          data = await runtime.catalog('charts');
        } else if (this.activeCategory === '精品歌单') {
          data = await runtime.catalog('high-quality-playlists', undefined, { limit: 50, before: this.cursor });
        } else {
          data = await runtime.catalog('playlists', undefined, { category: this.activeCategory, offset: this.cursor, limit: 50 });
        }
        if (version !== this.request) return;
        const items = data.playlists || data.charts;
        if (!Array.isArray(items)) throw new Error('歌单来源返回了无效数据，请重试。');
        const existing = new Set(this.playlists.map(item => String(item.id)));
        const additions = items.filter(item => !existing.has(String(item.id)));
        this.playlists = append ? [...this.playlists, ...additions] : items;
        this.hasMore = Boolean(data.more) && additions.length > 0;
        this.cursor = data.nextCursor || this.playlists.length;
        this.showLoadMoreButton = true;
      } catch (error) {
        if (version === this.request) this.error = error.message || '歌单加载失败，请重试。';
      } finally {
        if (version === this.request) this.loadingMore = false;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.explore-page { padding-top: 8px; }
.explore-toolbar { display: flex; align-items: center; flex-wrap: wrap; gap: 10px 18px; }
.explore-toolbar ::v-deep .catalog-platform { margin: 0; flex-shrink: 0; }
.buttons {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.button {
  user-select: none;
  cursor: pointer;
  padding: 8px 12px;
  margin: 0;
  min-height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
  border-radius: 9px;
  background-color: var(--color-secondary-bg);
  color: var(--color-secondary);
  transition: 0.2s;

  &:hover {
    background-color: var(--color-primary-bg);
    color: var(--color-primary);
  }
}
.button.active {
  background-color: var(--color-primary-bg);
  color: var(--color-primary);
}
.panel {
  margin-top: 10px;
  background: var(--color-secondary-bg);
  border-radius: 10px;
  padding: 8px;
  color: var(--color-text);

  .big-cat {
    display: flex;
    margin-bottom: 32px;
  }

  .name {
    font-size: 24px;
    font-weight: 700;
    opacity: 0.68;
    margin-left: 24px;
    min-width: 54px;
    height: 26px;
    margin-top: 8px;
  }
  .cats {
    margin-left: 24px;
    display: flex;
    flex-wrap: wrap;
  }
  .cat {
    user-select: none;
    margin: 4px 0px 0 0;
    display: flex;
    // justify-content: center;
    align-items: center;
    font-weight: 500;
    font-size: 16px;
    transition: 0.2s;
    min-width: 98px;

    span {
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      padding: 6px 12px;
      height: 26px;
      border-radius: 10px;
      opacity: 0.88;
      &:hover {
        opacity: 1;
        background-color: var(--color-primary-bg);
        color: var(--color-primary);
      }
    }
  }
  .cat.active {
    color: var(--color-primary);
  }
}

.playlists {
  margin-top: 18px;
}

.load-more {
  display: flex;
  justify-content: center;
  margin-top: 32px;
}

.button.more {
  .svg-icon {
    height: 24px;
    width: 24px;
  }
}
@media(max-width:760px) { .explore-toolbar { gap: 10px; } .buttons { flex-basis: 100%; flex-wrap: nowrap; overflow-x: auto; padding-bottom: 4px; } .button { flex-shrink: 0; } .playlists { margin-top: 12px; } }
</style>

